/**
 * The record's settings (One testing backlog C1). A command that declares the facts it `edits`
 * shows beside those facts instead of in the header's row of commands (and a row command that edits a column, in its
 * cell instead of among the row's commands, `rowSettingsOf`): an edit button for a value (Customer PO,
 * Contact), a switch for an on/off fact (Deliver together, Spend reporting). Commands stay commands: Claude runs them
 * the same way, and the shell still opens them.
 */
import type {
  Command,
  JsonSchema,
  ManifestColumn,
  ManifestCommand,
} from "./contract";
import type { ManifestIndex } from "./manifest";
import type { PresentedChildRow, PresentedFact } from "./record";

export type Setting =
  | {
      kind: "edit";
      /** The commands that change the fact, in declared order: the first is the edit button, the rest links (Clear…). */
      commands: Command[];
    }
  | {
      kind: "switch";
      isOn: boolean;
      /** What turns it on and off: a pair of commands, or one command with a boolean field (`field`). */
      turnOn: { command: Command; body: Record<string, unknown> } | null;
      turnOff: { command: Command; body: Record<string, unknown> } | null;
    };

export interface RecordSettings {
  /** By fact field. */
  byField: ReadonlyMap<string, Setting>;
  /** The commands shown as settings, so the header leaves them out. */
  shownAsSettings: ReadonlySet<string>;
}

/**
 * The one boolean field of a command's request, when that is all it asks for. A hidden field (a dry run's stale guard,
 * `expected_draft_version`) is not asked: the dry run's `guard` fills it.
 */
function soleBooleanField(declared: ManifestCommand): string | null {
  const properties = Object.entries(declared.request_schema?.properties ?? {})
    .filter(
      ([name, schema]) =>
        name !== "request_id" &&
        name !== "confirmed" &&
        !schema["x-cis-field"]?.hidden,
    )
    .map(([name, schema]) => [name, schema] as const);
  if (properties.length !== 1) return null;
  const [name, schema] = properties[0]!;
  return isBoolean(schema) ? name : null;
}

function isBoolean(schema: JsonSchema): boolean {
  if (schema.type === "boolean") return true;
  return (schema.anyOf ?? []).some((s) => s.type === "boolean");
}

/** An on/off fact's value, whatever its case: the customer view sends `Open`, `Blocked`, `On` and `Off`. */
export function isOnValue(raw: unknown): boolean | null {
  if (typeof raw === "boolean") return raw;
  if (typeof raw !== "string") return null;
  const value = raw.trim().toLowerCase();
  if (value === "on" || value === "open" || value === "true") return true;
  if (value === "off" || value === "blocked" || value === "false") return false;
  return null;
}

export function settingsOf(
  index: ManifestIndex,
  facts: readonly Pick<PresentedFact, "field" | "raw">[],
  commands: readonly Command[],
  /**
   * Commands that set a fact without declaring `edits`, by name: the tabbed record's placement overrides
   * (`overriddenEditsOf`, Change trading company…), drawn as the fact's pencil there only.
   */
  extraEdits: ReadonlyMap<string, readonly string[]> = new Map(),
): RecordSettings {
  const byField = new Map<string, Setting>();
  const shownAsSettings = new Set<string>();
  for (const fact of facts) {
    const editing = commands.filter(
      (c) =>
        index.commandOf(c.name).edits?.fields.includes(fact.field) ||
        extraEdits.get(c.name)?.includes(fact.field),
    );
    if (editing.length === 0) continue;
    const declaredOf = (c: Command) => index.commandOf(c.name);
    const on = editing.find((c) => declaredOf(c).edits?.value === true) ?? null;
    const off =
      editing.find((c) => declaredOf(c).edits?.value === false) ?? null;
    const single = editing.find(
      (c) =>
        declaredOf(c).edits?.value === undefined &&
        soleBooleanField(declaredOf(c)) !== null,
    );
    if (on || off) {
      // A pair: the command that is left out is the state the record is already in.
      const fromRaw = isOnValue(fact.raw);
      byField.set(fact.field, {
        kind: "switch",
        isOn: fromRaw ?? (off !== null && on === null),
        turnOn: on ? { command: on, body: {} } : null,
        turnOff: off ? { command: off, body: {} } : null,
      });
      for (const c of [on, off]) if (c) shownAsSettings.add(c.name);
    } else if (single) {
      const field = soleBooleanField(declaredOf(single))!;
      byField.set(fact.field, {
        kind: "switch",
        isOn: isOnValue(fact.raw) === true,
        turnOn: { command: single, body: { [field]: true } },
        turnOff: { command: single, body: { [field]: false } },
      });
      shownAsSettings.add(single.name);
    } else {
      byField.set(fact.field, { kind: "edit", commands: editing });
      for (const c of editing) shownAsSettings.add(c.name);
    }
  }
  return { byField, shownAsSettings };
}

/**
 * A child row's settings: a row command whose `edits` names one of the table's columns is drawn in that cell (a switch
 * for Optional extra…) instead of among the row's commands, as a record's are beside its facts.
 */
export function rowSettingsOf(
  index: ManifestIndex,
  columns: readonly Pick<ManifestColumn, "field">[],
  row: Pick<PresentedChildRow, "cellRaws" | "commands">,
): RecordSettings {
  return settingsOf(
    index,
    columns.map((column, n) => ({ field: column.field, raw: row.cellRaws[n] })),
    row.commands,
  );
}
